/*
 * Convex redacts an error's `message` on the client in production and keeps
 * the operator-facing text in the ConvexError payload instead, so reading
 * `message` alone renders every application error as "Server Error". The
 * payload is read structurally rather than by importing Convex, which this
 * package does not otherwise depend on.
 */
export function applicationErrorData(err: unknown): string | null {
  if (err == null || typeof err !== "object" || !("data" in err)) {
    return null;
  }
  const data: unknown = (err as { data: unknown }).data;
  if (typeof data === "string") {
    return data.trim() === "" ? null : data;
  }
  if (data == null || typeof data !== "object" || !("message" in data)) {
    return null;
  }
  const message: unknown = (data as { message: unknown }).message;
  return typeof message === "string" && message.trim() !== "" ? message : null;
}

export function getErrorMessage(err: unknown, fallback?: string): string {
  const data = applicationErrorData(err);
  if (data != null) {
    return data;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback ?? String(err);
}

// Convex frames a server throw for the client: a function tag, a request
// id, `Server Error`, then `Uncaught Error: <message>` and the server's
// stack. None of that frame is for a person to read.
const CONVEX_FRAME = /^\s*(\[CONVEX [^\]]*\]\s*)?(\[Request ID: [^\]]*\]\s*)?/;
const STACK_LINE = /^\s+at |^\s*Called by client$/;
const UNCAUGHT = /^(Server Error\s*)?(Uncaught \w*: )?/;

/** The part of an error a person reads: the ConvexError payload, or the
 * thrown message stripped of Convex's frame and the server stack. A
 * redacted server error has no such part and reads as `fallback`. */
export function readableErrorMessage(err: unknown, fallback: string): string {
  const data = applicationErrorData(err);
  if (data != null) {
    return data;
  }
  if (!(err instanceof Error)) {
    return fallback;
  }
  const text = err.message
    .replace(CONVEX_FRAME, "")
    .split("\n")
    .filter((line) => !STACK_LINE.test(line))
    .join("\n")
    .replace(UNCAUGHT, "")
    .trim();
  return text === "" ? fallback : text;
}

// The fields attached to the instance (an SDK's `status`, a parsed body) as
// entries, which is how every logger consumes them; `message` and `stack` are
// non-enumerable and stay with the Error.
export function errorEnumerableEntries(err: Error): [string, unknown][] {
  return Object.keys(err).map((key): [string, unknown] => [
    key,
    Reflect.get(err, key),
  ]);
}
